import React from 'react'

import { Box, SortDirection, Table, Text } from '@factorialco/deprecated-design-system'
import { orderBy } from 'lodash'

import type { WithMultiSelectStateChildrenProps } from 'modules/core/components/WithMultiSelectState'
import createComputed from 'modules/core/hooks/createComputed'
import i18n from 'modules/core/lib/i18n'

import { ProjectsToImport } from 'modules/project_management/lib/types'
import { ImportedProjectProps } from 'modules/project_management/lib/types'

import Body from './_Body'
import { LegalEntity } from './_Body/CellContent'
import Header from './_Header'

type WithMultiSelectProps = WithMultiSelectStateChildrenProps<ProjectsToImport>

interface ImportsTableProps {
  onToggleSelectedItem: WithMultiSelectProps['onToggleSelectedItem']
  onDeselectAll: () => void
  onSelectAll: () => void
  projectsList: ProjectsToImport[]
  selected: ProjectsToImport[]
  tabId: string
  legalEntities: LegalEntity[]
  defaultCurrency: string
}

const sortProjectsList = createComputed(
  (projectsList: ProjectsToImport[], attribute: string, direction: 'asc' | 'desc') =>
    orderBy(
      projectsList,
      (project) => {
        switch (attribute) {
          case 'subproject_names':
            return project.subproject_names.length
          default:
            return project[attribute as ImportedProjectProps]
        }
      },
      [direction]
    )
)

const ImportsTable = ({
  onToggleSelectedItem,
  onDeselectAll,
  onSelectAll,
  projectsList,
  selected,
  tabId,
  legalEntities,
  defaultCurrency,
}: ImportsTableProps) => {
  const [direction, setDirection] = React.useState<SortDirection>('asc')
  const [attribute, setAttribute] = React.useState<string>('project_name')

  const onSort = (attribute: string, direction: SortDirection) => {
    setDirection(direction)
    setAttribute(attribute)
  }

  const sortedJournalLines = sortProjectsList(projectsList, attribute, direction).get()

  const onClickAll = () => {
    if (projectsList.length === selected.length) {
      onDeselectAll()
    } else {
      onSelectAll()
    }
  }

  if (projectsList.length === 0) {
    let description

    if (tabId === 'ready') {
      description = i18n.t('project_management.imports.table.empty_messages.ready')
    } else {
      description = i18n.t('project_management.imports.table.empty_messages.errored')
    }

    return (
      <Box
        height="s200"
        background="grey200"
        flexDirection="row"
        gap="s8"
        paddingX="s24"
        textAlign="center"
        alignItems="center"
        justifyContent="center"
      >
        <Box maxWidth="s600">
          <Text weight="normal" size="300" color="grey800">
            {description}
          </Text>
        </Box>
      </Box>
    )
  }
  return (
    <Table autoWidth>
      <Header
        attribute={attribute}
        direction={direction}
        onClickAll={onClickAll}
        onSort={onSort}
        projectsList={projectsList}
        selected={selected}
        tabId={tabId}
      />
      <Body
        onToggleSelectedItem={onToggleSelectedItem}
        projectsList={sortedJournalLines}
        selected={selected}
        tabId={tabId}
        legalEntities={legalEntities}
        defaultCurrency={defaultCurrency}
      />
    </Table>
  )
}

export default ImportsTable
